import { PropsWithChildren, useEffect, useState } from 'react';
import AmazonLogo from '@/icons/amazon.svg?react';
import UpdatedIcon from '@/icons/updated.svg?react';
import { useLocation, useNavigate } from 'react-router';
import { Loader } from '@/components/Loader/Loader';
import Logo from '@/components/Logo/Logo';
import { getClientForAmazonConnect, postAmazonConnect } from '@/core/api';
import { PublicClient } from '@/core/types';
import { PublicRoutesEnum } from '@/core/enums';
import { STATE_CONSTANT } from '@/core/constants';

export default function AmazonConnect() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const state = params.get('state');
  const spapi_oauth_code = params.get('spapi_oauth_code');
  const selling_partner_id = params.get('selling_partner_id');

  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isError, setIsError] = useState(false);
  const [client, setClient] = useState<PublicClient>();

  useEffect(() => {
    if (!state) {
      navigate(PublicRoutesEnum.Page404, { replace: true });
      return;
    }

    if (spapi_oauth_code && selling_partner_id) {
      if (state.includes('setup-check') || state.includes(STATE_CONSTANT)) {
        navigate({ pathname: '/', search: location.search }, { replace: true });
        return;
      }

      Promise.allSettled([
        getClientForAmazonConnect(state),
        postAmazonConnect({
          seller_id: selling_partner_id,
          oauth_code: spapi_oauth_code,
          client_uuid: state,
        }),
      ]).then(([clientRes, postRes]) => {
        if (clientRes.status === 'fulfilled') {
          setClient(clientRes.value);
        }

        if (postRes.status === 'fulfilled') {
          setIsConnected(true);
        } else {
          setIsError(true);
        }

        setIsLoading(false);
      });
    } else {
      getClientForAmazonConnect(state)
        .then((res) => {
          setClient(res);
          setIsLoading(false);
        })
        .catch(() => {
          navigate(PublicRoutesEnum.Page404, { replace: true });
        });
    }
    // eslint-disable-next-line
  }, [state, spapi_oauth_code, selling_partner_id]);

  const footer = (
    <div className='mx-auto max-w-[448px] text-center'>
      <span>
        Wenn Du Fragen oder Probleme mit dem Login hast, schreib uns an
      </span>{' '}
      <a
        href='mailto:vat@countx.com?subject=Amazon-Verbindungsprobleme'
        className='text-ink hover:text-[#0d07dc]'
      >
        vat@countx.com
      </a>
    </div>
  );

  const button = (
    <a
      className='mb-12 flex h-12 w-full items-stretch overflow-hidden rounded-lg border border-solid border-[#ff9900] bg-[#ff9900] p-0 text-base tracking-[-0.005em] text-white no-underline transition-colors duration-[250ms] hover:border-[#f59300] hover:bg-[#f59300] hover:text-white lg:h-16 lg:text-h4'
      href={`https://sellercentral-europe.amazon.com/apps/authorize/consent?application_id=${
        import.meta.env.VITE_APP_ID
      }&state=${state}&version=beta`}
      data-testid='amazon-connect-btn'
    >
      <div className='flex w-12 items-center justify-center bg-white lg:w-16'>
        <AmazonLogo />
      </div>
      <div className='flex flex-grow items-center justify-center px-4'>
        Amazon verbinden
      </div>
    </a>
  );

  if (!state || isLoading) return <Loader />;

  if (isConnected) {
    return (
      <Layout title='Verbunden!'>
        <>
          <div className='mb-4 max-w-[488px] text-center text-base lg:text-h4 lg:tracking-[-0.01em]'>
            <span>Dein Amazon-Token wurde erfolgreich generiert.</span>
            <span className='hidden lg:inline'>
              <br />
            </span>
            <span>countX kann nun die Transaktionsdaten generieren.</span>
          </div>
          <div className='mb-4 text-base font-extrabold lg:mb-6 lg:text-h4 lg:tracking-[-0.01em]'>
            {client?.name}
          </div>
          <div className='flex w-full flex-grow flex-col items-center justify-between'>
            <div className='mb-12'>
              <UpdatedIcon />
            </div>
            {footer}
          </div>
        </>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout title='Ausfall der Verbindung' error={true}>
        <>
          <div className='mb-4 max-w-[488px] text-center text-base lg:text-h4 lg:tracking-[-0.01em]'>
            Aufgrund eines technischen Fehlers kann keine Verbindung zu Amazon
            hergestellt werden. Bitte versuche es erneut oder setze Dich mit
            unserem Manager in Verbindung.
          </div>
          <div className='mb-4 text-base font-extrabold lg:mb-6 lg:text-h4 lg:tracking-[-0.01em]'>
            {client?.name}
          </div>
          <div className='flex w-full flex-grow flex-col justify-between'>
            {button}
            {footer}
          </div>
        </>
      </Layout>
    );
  }

  if (!client) return null;

  return (
    <Layout title='Amazon-Daten verbinden'>
      <>
        <div className='mb-4 max-w-[488px] text-center text-base lg:text-h4 lg:tracking-[-0.01em]'>
          Um mit der Integration der Amazon Transaktionsdaten fortzufahren,
          benötigt countX einen gültigen Amazon-Token. Bitte logge Dich über den
          Link unten in Deinen Amazon-Seller Account ein, und wir
          synchronisieren ab sofort die Daten, die wir für unseren Service
          benötigen.
        </div>
        <div className='mb-4 text-base font-extrabold lg:text-h4 lg:tracking-[-0.01em]'>
          {client.name}
        </div>
        <div className='flex w-full flex-grow flex-col justify-between'>
          {button}
          {footer}
        </div>
      </>
    </Layout>
  );
}

const Layout = ({
  children,
  error,
  title,
}: PropsWithChildren<{
  error?: boolean;
  title: string;
}>) => {
  return (
    <div
      className='min-h-screen bg-gray-300 px-4 py-12 lg:px-6 lg:pt-[88px]'
      data-testid='amazon-connect'
    >
      <div className='relative mx-auto flex min-h-[480px] max-w-[340px] flex-grow flex-col overflow-hidden rounded-2xl bg-white px-4 pb-4 pt-6 lg:min-h-[720px] lg:max-w-[592px] lg:px-11 lg:py-6'>
        {error && (
          <div
            className='absolute left-0 top-0 h-2 w-full bg-red-500'
            data-testid='amazon-connect-error'
          />
        )}
        <div className='flex flex-grow flex-col items-center lg:mt-2'>
          <div className='mb-4 pb-4 lg:mb-12'>
            <Logo className='mx-auto w-[88px] lg:w-[108px]' />
          </div>
          <div
            className='mb-4 text-center text-h4 font-black lg:text-h2'
            data-testid='amazon-connect-title'
          >
            {title}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};
